/* eslint-disable max-lines */
import AppRouter from 'common/AppRouter';
import {
  getTeamPicker,
  handleTeamIdOnUrl
} from 'common/services/teams.service';
import {
  getCurrentIteration,
  navigateBookDemoClick,
  isSurveyFlow
} from 'common/utils/utils';
import PeriodPicker from 'components/PeriodPicker';
import { Flex, Margin } from 'components/base/layout-components';
import TreeTeamsDropdown from 'components/teams/TreeTeamsDropdown/TreeTeamsDropdown';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { settingsSelector } from 'state/selectors/settings';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import PulseIterationPicker from '../../views/root/views/home/views/pulse/components/PulseIterationPicker';
import UserProfile from '../ProfilePanel';
import AccountSettingBar from './AccountSettingBar';
import MissingIterations from './MissingIterations';
import OnboardingStepper from './OnboardingStepper';
import TopBarBreadcrumbs from './TopBarBreadcrumbs';
import {
  allTeams,
  handleTeamChange,
  METRICS_PAGE_PATHS
} from './TopBar.service';
import { StyledAppBar } from './TopBar.style';
import { useTopBarSettings } from './useTopbarSettings';
import useSettingsService from 'components/hooks/useSettingsService';
import { BookDemoTooltip } from '@linearb/linui-common';
import { daysBetweenDates } from '@linearb/linui-utils';
import { usePeriodPickerInitialValue } from './usePeriodPickerInitialValue';
import { PERIODS } from 'components/PeriodPicker/periods/constants';
import {
  CTA_TOOLTIP_TEXTS,
  TRACKING
} from 'views/root/views/home/views/dashboard/doraV2/dashboard.doraV2.freeTier.consts';
import * as TrackingService from 'common/services/tracking.service';
import useDoraFeatureFlags from 'views/root/views/home/hooks/useDoraFeatureFlags';
import { TRACKING as TRACKING_EVENTS } from 'common/constants/constants';
import useTeams from 'views/root/views/home/views/settings/views/ContributorsAndTeams/Teams/useTeams';
import { setSelectedTeamChangedFromTopBar } from 'state/actions/ui-state';
import { useTopBarFiltersStateByRoute } from './useTopBarFiltersStateByRoute';
import { PeriodPickerTooltip } from './PeriodPickerTooltip';
import { DisabledFilterTooltip } from './DisabledFilterTooltip';
import TeamGitScopeBanner from 'components/TeamGitScopeBanner';

// eslint-disable-next-line max-lines-per-function
const TopBar = props => {
  const {
    showDatePicker = true,
    showDisabledDatePicker = false,
    showTeamPicker,
    show30daysMessage,
    iterationsPickerData,
    uiState,
    currentTeams,
    selectedTeam = allTeams,
    DateSelectionChanged,
    showTabsNavigation,
    isOnTopOfModals,
    isTeamLicense,
    toggleModal,
    license,
    isOnboarding,
    selectTeamId,
    createRepositories,
    toggleSnackBar,
    gitIntegrations,
    setIsProcessingRepos,
    AdditionalTopBarComponent = null,
    sensorAuthorizations,
    hasIterations,
    shouldHidePickers,
    height,
    getRepositories,
    setOpenUserProfileWizard,
    isUserProfileWizardEnabled,
    disableTeamPicker,
    setDisableTeamPicker,
    showFilteredView = false,
    viewPath,
    gitScopeToggle,
    setGitScopeToggle
  } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const { onConfigureTeam } = useTeams();
  const { enableTeamSelection } = useDoraFeatureFlags();
  const { shouldLimitPeriodPicker } = useTopBarSettings(
    sensorAuthorizations,
    isTeamLicense
  );
  const { disableTeamPickerByRoute, disablePeriodPicker } =
    useTopBarFiltersStateByRoute();

  useEffect(() => {
    if (setDisableTeamPicker) {
      setDisableTeamPicker(disableTeamPickerByRoute);
    }
  }, [disableTeamPickerByRoute]);

  const {
    dates: { from, to },
    selectedIterationId,
    selectedTeamId
  } = uiState;

  const isLessThan30days = show30daysMessage && daysBetweenDates(from, to) < 30;

  const currentIteration = getCurrentIteration(
    iterationsPickerData,
    from,
    to,
    selectedIterationId
  );

  const currentPeriod = usePeriodPickerInitialValue(
    shouldLimitPeriodPicker,
    uiState,
    currentIteration
  );

  const { getSettingOrDefinitionValue } = useSettingsService();
  const allSettings = useSelector(settingsSelector);
  const isGroup = useSelector(isSelectedTeamParentSelector);

  useEffect(() => {
    handleTeamIdOnUrl(
      selectedTeam.id > 0 ? selectedTeam : null,
      history,
      isTeamLicense
    );
  }, [selectedTeam]);

  const { location } = history;

  const showIterationPicker = showDatePicker || showDisabledDatePicker;

  const showMissingIterationsIndication =
    !hasIterations &&
    (showIterationPicker || AdditionalTopBarComponent === PulseIterationPicker);

  const clickAndTrackBookDemo = () => {
    TrackingService.logEvent(TRACKING.EVENTS.CTA_CLICK, {
      page: TRACKING_EVENTS.PAGE_TABS.TOPBAR,
      cta: TRACKING.CTA_TYPE.TOOLTIP,
      intent: TRACKING.CTA_INTENT.BOOK_A_DEMO,
      source_url: window.location.href
    });
    navigateBookDemoClick();
  };

  const clickAndTrackManageTeams = () => {
    TrackingService.logEvent(TRACKING.EVENTS.CTA_CLICK, {
      page: TRACKING_EVENTS.PAGE_TABS.TOPBAR,
      cta: TRACKING.CTA_TYPE.TOOLTIP,
      intent: TRACKING.CTA_INTENT.MANAGE_TEAMS,
      source_url: window.location.href
    });
    history.replace(`${AppRouter.TEAMS_SETTINGS.path}?isOrgSettings=true`);
  };

  const bookDemoTooltipProps = isTeamLicense
    ? {
        message: CTA_TOOLTIP_TEXTS.TOPBAR,
        buttonText: CTA_TOOLTIP_TEXTS.BOOK_A_DEMO,
        onClick: clickAndTrackBookDemo,
        disabled: !shouldLimitPeriodPicker
      }
    : {
        message: CTA_TOOLTIP_TEXTS.TOPBAR_PAID,
        buttonText: CTA_TOOLTIP_TEXTS.MANAGE_TEAMS,
        onClick: clickAndTrackManageTeams,
        disabled: !shouldLimitPeriodPicker
      };

  const hiddenPeriods = shouldLimitPeriodPicker
    ? []
    : [PERIODS.PAST_90_DAYS.periodKey];

  const isMetricsPage = METRICS_PAGE_PATHS.includes(
    `/${location?.pathname?.split('/')[1]}`
  );

  if (isSurveyFlow()) return <div />;

  return (
    <StyledAppBar
      color='inherit'
      position='relative'
      height={height}
      {...(isOnTopOfModals ? { isontop: isOnTopOfModals.toString() } : {})}
      {...(isOnboarding ? { isonboarding: isOnboarding.toString() } : {})}
    >
      <Flex
        direction='row'
        items='center'
        width='100%'
        height='100%'
        data-cy='top-bar'
      >
        {(isOnboarding || !!allSettings.length) && (
          <Flex grow='1' items='center' height='100%'>
            {isOnboarding ? (
              <OnboardingStepper
                location={location}
                isOnTopOfModals={isOnTopOfModals}
              />
            ) : (
              <Flex
                height='100%'
                justify='center'
                content='center'
                items='center'
                grow='1'
              >
                <Flex grow='1'>
                  <TopBarBreadcrumbs />
                </Flex>

                {showFilteredView && (
                  <TeamGitScopeBanner
                    viewPath={viewPath}
                    gitScopeToggle={gitScopeToggle}
                    setGitScopeToggle={setGitScopeToggle}
                  />
                )}

                {showTeamPicker ? (
                  !isTeamLicense ? (
                    <Margin r='small'>
                      <DisabledFilterTooltip enabled={disableTeamPicker}>
                        <TreeTeamsDropdown
                          disabled={disableTeamPicker}
                          width='23rem'
                          selectedTeamIds={[selectedTeamId]}
                          onChange={selectedTeams => {
                            dispatch(
                              setSelectedTeamChangedFromTopBar(isMetricsPage)
                            );
                            handleTeamChange({
                              team: selectedTeams[0],
                              selectTeamId,
                              history,
                              isTeamLicense
                            });
                          }}
                          isSelectRootEnabled
                          isOpenParentsOfSelected
                          size='large'
                          dataCyPrefix='top-bar'
                        />
                      </DisabledFilterTooltip>
                    </Margin>
                  ) : (
                    getTeamPicker({
                      isClickable:
                        enableTeamSelection && currentTeams?.length > 0,
                      selectedTeam,
                      onSingleModeClick: () =>
                        onConfigureTeam({ team: selectedTeam })
                    })
                  )
                ) : null}

                {showIterationPicker ? (
                  <BookDemoTooltip {...bookDemoTooltipProps}>
                    <PeriodPickerTooltip
                      disablePeriodPicker={disablePeriodPicker}
                      isLessThan30days={isLessThan30days}
                    >
                      <PeriodPicker
                        currentPeriod={currentPeriod}
                        onChange={DateSelectionChanged}
                        size='large'
                        width='20rem'
                        showCalendarIcon={true}
                        disabled={
                          shouldLimitPeriodPicker || disablePeriodPicker
                        }
                        hiddenPeriods={hiddenPeriods}
                        show30daysMessage={isLessThan30days}
                      />
                    </PeriodPickerTooltip>
                  </BookDemoTooltip>
                ) : null}
                {AdditionalTopBarComponent && !shouldHidePickers && (
                  <AdditionalTopBarComponent
                    {...props}
                    getCurrentIteration={getCurrentIteration}
                  />
                )}
                {showMissingIterationsIndication && (
                  <MissingIterations
                    routeToTeamSettings={() =>
                      history.push({
                        pathname: AppRouter.PROJECT_MANAGEMENT.path
                      })
                    }
                  />
                )}
                <Flex
                  justify='flex-end'
                  content='center'
                  items='center'
                  ml='3rem'
                  height='100%'
                >
                  <Flex items='center' height='100%'>
                    <AccountSettingBar
                      selectedTeam={selectedTeam}
                      showAccountSettingBar={showTabsNavigation}
                      history={history}
                      createRepositories={createRepositories}
                      toggleModal={toggleModal}
                      toggleSnackBar={toggleSnackBar}
                      gitIntegrations={gitIntegrations}
                      setIsProcessingRepos={setIsProcessingRepos}
                      license={license}
                      isGroup={isGroup}
                      getSettingOrDefinitionValue={getSettingOrDefinitionValue}
                      getRepositories={getRepositories}
                    />
                    <Margin l='small'>
                      <UserProfile
                        isOnTopOfModals={isOnTopOfModals}
                        setOpenUserProfileWizard={setOpenUserProfileWizard}
                        isUserProfileWizardEnabled={isUserProfileWizardEnabled}
                      />
                    </Margin>
                  </Flex>
                </Flex>
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </StyledAppBar>
  );
};

export default TopBar;
